import React, { useContext, useState } from "react";
import ReactDOM from "react-dom";
import GooglePayButton from "@google-pay/button-react";
import countryToCurrency from "country-to-currency";
import AppLabelsContext from "../../../../App/AppLabelsContext";
import CurrencyContext from "../../../../App/CurrencyContext";

const PaymentMethodGooglePayButton = (props) => {
  const {
    activeOrderStore,
    orderTotal,
    validateOrderTime,
    appLanguage,
    submitGooglePayOrder,
    requiresOrderConfirmation,
    requestOrderConfirmation,
  } = props;

  const [preventDefaultBehaviour, setPreventDefaultBehaviour] = useState(true);
  const currencyContext = useContext(CurrencyContext);

  const isLocalhost =
    process.env.NODE_ENV === "development" ||
    window.location.hostname.includes("alpha") ||
    window.location.hostname.includes("beta") ||
    window.location.hostname.includes("dilbert");
  const storeWithVexilorConfig = activeOrderStore.vexilorConfig;
  const allowedCardNetworks = storeWithVexilorConfig.payment_methods["CREDIT"]
    .map((allowedCards) => allowedCards[0])
    .filter((entry) => !entry.toLowerCase().includes("other"));

  const GOOGLE_PAY_PRODUCTION_ID = "3336759522887621560";
  const GOOGLE_PAY_TEST_ID = "BCR2DN4TZLH43BBN";

  const currencyCode =
    countryToCurrency[
      activeOrderStore.country === "UK" ? "GB" : activeOrderStore.country
    ].toString();

  const googlePayConfigGateway = storeWithVexilorConfig.google_pay_config.gateway;

  const googlePayConfigGatewayMechantId =
    storeWithVexilorConfig.google_pay_config.gateway_merchant_id;

  const appLabels = useContext(AppLabelsContext);

  const continueToGooglePay = () => {
    setPreventDefaultBehaviour(false);
    setTimeout(() => {
      const googlePayButton = document.getElementsByClassName("gpay-button")[0];
      if (googlePayButton) googlePayButton.click();
    }, 0);
  };

  const getTokenizationSpecification = () => {
    let tokenizationSpecification = {};
    if (googlePayConfigGateway.toUpperCase() === "DIRECT") {
      // Direct integration - gateway merchant ID is our public key instead.
      tokenizationSpecification = {
        type: "DIRECT",
        parameters: {
          protocolVersion: "ECv2",
          publicKey: googlePayConfigGatewayMechantId,
        },
      };
    } else {
      // Gateway integration
      tokenizationSpecification = {
        type: "PAYMENT_GATEWAY",
        parameters: {
          gateway: googlePayConfigGateway,
          gatewayMerchantId: googlePayConfigGatewayMechantId,
        },
      };
    }
    return tokenizationSpecification;
  };

  return ReactDOM.createPortal(
    <div className="google-pay-checkout-button__container">
      <GooglePayButton
        aria-label={appLabels["order"]["pay-with-google-pay"]}
        environment={isLocalhost ? "TEST" : "PRODUCTION"}
        buttonSizeMode="fill"
        paymentRequest={{
          apiVersion: 2,
          apiVersionMinor: 0,
          allowedPaymentMethods: [
            {
              type: "CARD",
              parameters: {
                allowedAuthMethods: ["PAN_ONLY", "CRYPTOGRAM_3DS"],
                allowedCardNetworks: allowedCardNetworks,
              },
              tokenizationSpecification: getTokenizationSpecification(),
            },
          ],
          merchantInfo: {
            merchantId: isLocalhost ? GOOGLE_PAY_TEST_ID : GOOGLE_PAY_PRODUCTION_ID,
            merchantName: "Givex Corp",
          },
          transactionInfo: {
            totalPriceStatus: "FINAL",
            totalPriceLabel: "Total",
            totalPrice: Number(orderTotal / currencyContext.currencyData[1])
              .toFixed(2)
              .toString(),
            currencyCode: currencyCode,
            countryCode: activeOrderStore.country === "UK" ? "GB" : activeOrderStore.country,
          },
          shippingAddressRequired: false,
          callbackIntents: ["PAYMENT_AUTHORIZATION"],
          emailRequired: true,
        }}
        onClick={async (event) => {
          if (preventDefaultBehaviour) {
            event.preventDefault();
            const isValidOrderTime = await validateOrderTime();
            if (isValidOrderTime) {
              if (requiresOrderConfirmation) {
                requestOrderConfirmation(continueToGooglePay);
              } else {
                continueToGooglePay();
              }
              return true;
            }
          }
        }}
        onCancel={(reason) => {
          setPreventDefaultBehaviour(true);
        }}
        onLoadPaymentData={(paymentRequest) => {
          submitGooglePayOrder(paymentRequest);
        }}
        onPaymentAuthorized={(paymentData) => {
          if (paymentData) {
            return { transactionState: "SUCCESS" };
          } else {
            return {
              transactionState: "ERROR",
              error: {
                reason: "PAYMENT_DATA_INVALID",
                message: "Cannot pay with payment credentials",
                intent: "PAYMENT_AUTHORIZATION",
              },
            };
          }
        }}
        buttonLocale={appLanguage}
        buttonColor="default"
        buttonType="checkout"
      />
    </div>,
    document.body
  );
};

export default PaymentMethodGooglePayButton;
